import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DateTime } from 'luxon';

@Component({
  selector: 'pt-luxon-date-picker',
  templateUrl: 'pt-luxon-date-picker.html'
})

export class PtLuxonDatePickerComponent {
  public _luxonDate: DateTime;
  public _max: DateTime;
  public _min: DateTime;
  placeholder: string = 'DD/MM/YYYY';

  dateMask = [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
  dateShow: string;
  dateFilter = (date: DateTime): boolean => this.isWithinBounds(date);

  constructor() {
    this.dateShow = this._luxonDate ? this._luxonDate.toFormat('dd/MM/yyyy') : null;
  }

  @Input() disabled: boolean = false;
  @Input() name: string; // form input name
  @Input() inputWidth: number; // width in px
  @Input() startAtDate: DateTime; // view when you open the picker will start here, unless _luxonDate is set
  @Input() tabIndex: number;

  // 1 way binding for max date
  @Input()
  set max(value: DateTime) {
    if (value) {
      if (this._min == null || value >= this._min) {
        this._max = value;
      }
    } else {
      this._max = null;
    }
  }

  // 1 way binding for min date
  @Input()
  set min(value: DateTime) {
    if (value) {
      if (this._max == null || value <= this._max) {
        this._min = value;
      }
    } else {
      this._min = null;
    }
  }

  @Input()
  set date(value: DateTime) {
    if (value) {
      this._luxonDate = value;
      this.dateShow = this._luxonDate.toFormat('dd/MM/yyyy');
    } else {
      this._luxonDate = null;
      this.dateShow = null;
    }
  }

  // 2 way binding for date property
  @Output() dateChange: EventEmitter<DateTime> = new EventEmitter();
  @Output() valueChange: EventEmitter<any> = new EventEmitter();

  async onDateChange(value: any) {
    if (!value || !this.isWithinBounds(value)) {
      this.dateShow = this._luxonDate ? this._luxonDate.toFormat('dd/MM/yyyy') : null;
      return;
    }

    // date picker will set the hour/minutes to 0, we don't want to overwrite these though, only the date part
    if (this._luxonDate) {
      if (!value) {
        this._luxonDate = null;
      } else {
        this._luxonDate = this._luxonDate.set({
          year: value.year,
          month: value.month,
          day: value.day,
          hour: this._luxonDate.hour,
          minute: this._luxonDate.minute
        });
      }
    } else {
      this._luxonDate = value;
    }

    // todo - could refactor this to lookup in-app tz change and change the luxon date zone (keep local time)
    // for now we do this after selecting

    this.dateShow = this.addZeros(value.day) + '/' + this.addZeros(value.month) + '/' + this.addZerosToYear(value.year);
    this.dateChange.emit(this._luxonDate);
  }

  async onDateShowChange(value: string) {
    this.regexChange(value);

    if (!value || value.length == 0) {
      this._luxonDate = null;
      this.dateShow = null;
      this.dateChange.emit(this._luxonDate);
      return;
    }

    if (value.length !== 10 || value.includes('_')) {
      return;
    }

    if (this._luxonDate) {
      const year = parseInt(value.substring(6, 10));
      const month = parseInt(value.substring(3, 5));
      const day = parseInt(value.substring(0, 2));

      const newDate = this._luxonDate.set({
        year: year,
        month: month,
        day: day,
        hour: 0,
        minute: 0
      });

      if (this.isWithinBounds(newDate)) {
        this._luxonDate = newDate.set({
          year: year,
          month: month,
          day: day,
          hour: this._luxonDate.hour,
          minute: this._luxonDate.minute
        });
      } else {
        this._luxonDate = null;
      }
    } else {
      const newDate = DateTime.fromFormat(value, 'dd/MM/yyyy');
      this._luxonDate = this.isWithinBounds(newDate) ? newDate : null;
    }

    // todo - could refactor this to lookup in-app tz change and change the luxon date zone (keep local time)
    // for now we do this after selecting

    this.dateChange.emit(this._luxonDate);
  }

  // this will emit the input date, but be aware the time portion of the date will be set to 00:00
  onValueChange(date: DateTime) {
    this.valueChange.emit(date);
  }

  private isWithinBounds(date: DateTime): boolean {
    if (!date || !date.isValid) {
      return false;
    }

    const day = DateTime.utc(date.year, date.month, date.day).toMillis();
    const minimumDay = this._min
      ? DateTime.utc(this._min.year, this._min.month, this._min.day).toMillis()
      : null;
    const maximumDay = this._max
      ? DateTime.utc(this._max.year, this._max.month, this._max.day).toMillis()
      : null;

    return (minimumDay == null || day >= minimumDay) &&
      (maximumDay == null || day <= maximumDay);
  }

  // used for removing placeholder if date is clicked, or has a value (by default in material datepicker it will go to the top of the input otherwise)
  checkPlaceHolder() {
    // if (this.placeholder) {
    //   this.placeholder = null;
    //   return;
    // } else {
    //   this.placeholder = 'DD/MM/YYYY';
    //   return;
    // }
  }

  resetRegex() {
    this.dateMask = [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '0') {
      this.dateMask[1] = new RegExp('[1-9]')
    }

    if (inputTime.charAt(3) === '1') {
      this.dateMask[4] = new RegExp('[0-2]');
    } else if (inputTime.charAt(3) === '0') {
      this.dateMask[4] = new RegExp('[1-9]')
    } else {
      this.dateMask[4] = new RegExp('\\d');
    }
  }

  addZeros(value: number) {
    if (value < 10) {
      return '0' + value;
    }

    return value;
  }

  addZerosToYear(value: number) {
    if (value < 10) {
      return '000' + value;
    }

    if (value < 100) {
      return '00' + value;
    }

    if (value < 1000) {
      return '0' + value;
    }

    return value;
  }
}



// WEBPACK FOOTER //
// ./src/pages/shared/components/pt-luxon-date-picker/pt-luxon-date-picker.ts